import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Mail, Lock, User, Eye, EyeOff, X, Loader2, ArrowLeft, CheckCircle, ExternalLink, Check } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const GOOGLE_AUTH_URL = "https://auth.emergentagent.com/";

// Detect in-app browsers (Instagram, Facebook, Messenger, etc.)
function isInAppBrowser() {
  const ua = navigator.userAgent || navigator.vendor || window.opera;
  // Check for common in-app browser signatures
  return /FBAN|FBAV|Instagram|Messenger|Twitter|Line|KAKAOTALK|Snapchat|Pinterest/i.test(ua);
}

// Get the app name for display
function getInAppBrowserName() {
  const ua = navigator.userAgent || navigator.vendor || window.opera;
  if (/Instagram/i.test(ua)) return "Instagram";
  if (/FBAN|FBAV/i.test(ua)) return "Facebook";
  if (/Messenger/i.test(ua)) return "Messenger";
  if (/Twitter/i.test(ua)) return "Twitter";
  if (/Line/i.test(ua)) return "Line";
  if (/KAKAOTALK/i.test(ua)) return "KakaoTalk";
  if (/Snapchat/i.test(ua)) return "Snapchat";
  if (/Pinterest/i.test(ua)) return "Pinterest";
  return "this app";
}

// Use XMLHttpRequest to avoid fetch interception issues
function xhrRequest(url, method, body, withCredentials = true, extraHeaders = {}) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(method, url, true);
    xhr.setRequestHeader("Content-Type", "application/json");
    
    // Send auth token via header for cross-domain requests
    const token = localStorage.getItem("session_token");
    if (token) {
      xhr.setRequestHeader("Authorization", `Bearer ${token}`);
    }
    
    // Add any extra headers (like visitor ID)
    Object.entries(extraHeaders).forEach(([key, value]) => {
      xhr.setRequestHeader(key, value);
    });
    
    xhr.withCredentials = withCredentials;
    
    xhr.onload = function() {
      try {
        const data = JSON.parse(xhr.responseText);
        // Store session token if returned
        if (data.session_token) {
          localStorage.setItem("session_token", data.session_token);
        }
        resolve({ status: xhr.status, data });
      } catch (e) {
        resolve({ status: xhr.status, data: {} });
      }
    };
    
    xhr.onerror = function() {
      reject(new Error("Network error. Please check your connection."));
    };
    
    xhr.send(body ? JSON.stringify(body) : null);
  });
}

// Forgot Password Modal Component
export function ForgotPasswordModal({ isOpen, onClose, onBackToLogin }) {
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      const { status, data } = await xhrRequest(`${API}/auth/forgot-password`, "POST", { email });
      
      if (status >= 400) {
        const errorMsg = typeof data?.detail === 'string' 
          ? data.detail 
          : "Failed to send reset email";
        throw new Error(errorMsg);
      }
      
      setSuccess(true);
    } catch (err) {
      const errorMessage = typeof err === 'string' 
        ? err 
        : (err?.message || "An error occurred. Please try again.");
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 animate-fade-in"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div 
          className="bg-gradient-to-br from-zinc-900/95 via-zinc-900/90 to-zinc-800/80 backdrop-blur-xl border border-zinc-700/50 rounded-2xl w-full max-w-md p-6 animate-scale-in shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <button
              onClick={onBackToLogin}
              className="flex items-center gap-2 text-zinc-400 hover:text-white transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="text-sm">Back to login</span>
            </button>
            <button
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-zinc-800 transition-colors"
            >
              <X className="w-5 h-5 text-zinc-400" />
            </button>
          </div>

          {success ? (
            <div className="text-center py-6">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-500/20 flex items-center justify-center">
                <CheckCircle className="w-8 h-8 text-green-400" />
              </div>
              <h2 className="text-xl font-bold text-white mb-2">Check your email</h2>
              <p className="text-zinc-400 text-sm mb-6">
                If an account exists with <span className="text-white">{email}</span>, we've sent a password reset link.
              </p>
              <p className="text-zinc-500 text-xs">
                Didn't receive the email? Check your spam folder or try again.
              </p>
              <button
                onClick={onBackToLogin}
                className="mt-6 px-6 py-2 bg-white text-black font-semibold rounded-xl hover:bg-zinc-100 transition-all"
              >
                Back to login
              </button>
            </div>
          ) : (
            <>
              <h2 className="text-xl font-bold text-white mb-2">Reset your password</h2>
              <p className="text-zinc-400 text-sm mb-6">
                Enter your email address and we'll send you a link to reset your password.
              </p>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-xs font-semibold text-zinc-400 uppercase tracking-wider mb-2">
                    Email
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@example.com"
                      required
                      className="w-full bg-black border border-zinc-800 rounded-xl pl-10 pr-4 py-3 text-white placeholder-zinc-600 focus:outline-none focus:border-zinc-600 transition-all"
                    />
                  </div>
                </div>

                {error && (
                  <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 text-red-400 text-sm">
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full py-3 bg-white text-black font-bold rounded-xl hover:bg-zinc-100 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    "Send reset link"
                  )}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </>
  );
}

export function AuthModal({ isOpen, onClose, onSuccess, initialMode = "login" }) {
  const [mode, setMode] = useState(initialMode); // "login", "signup", "forgot", or "verify"
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [forgotSuccess, setForgotSuccess] = useState(false);
  const [emailError, setEmailError] = useState(null);
  const [isValidatingEmail, setIsValidatingEmail] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [verificationCode, setVerificationCode] = useState("");
  const [pendingEmail, setPendingEmail] = useState("");
  
  // Check for in-app browser
  const inAppBrowser = isInAppBrowser();
  const appName = getInAppBrowserName();

  // Validate email on blur (only for signup)
  const validateEmail = async (emailToValidate) => {
    if (mode !== "signup" || !emailToValidate) {
      setEmailError(null);
      return;
    }
    
    // Basic client-side validation first
    const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!emailPattern.test(emailToValidate)) {
      setEmailError("Please enter a valid email address");
      return;
    }
    
    // Server-side validation for typos and disposable domains
    setIsValidatingEmail(true);
    try {
      const { status, data } = await xhrRequest(`${API}/auth/validate-email`, "POST", { email: emailToValidate });
      if (status === 200 && !data.valid) {
        setEmailError(data.message);
      } else {
        setEmailError(null);
      }
    } catch (err) {
      // Don't block signup if validation fails
      setEmailError(null);
    } finally {
      setIsValidatingEmail(false);
    }
  };

  const handleEmailChange = (e) => {
    setEmail(e.target.value);
    // Clear error when user starts typing
    if (emailError) setEmailError(null);
  };

  const handleEmailBlur = () => {
    if (mode === "signup" && email) {
      validateEmail(email);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    // For signup, validate email first
    if (mode === "signup") {
      const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
      if (!emailPattern.test(email)) {
        setError("Please enter a valid email address");
        setIsLoading(false);
        return;
      }
      
      if (emailError) {
        setError(emailError);
        setIsLoading(false);
        return;
      }
      
      if (!agreedToTerms) {
        setError("Please agree to the Terms of Service and Privacy Policy");
        setIsLoading(false);
        return;
      }
    }

    // Handle forgot password
    if (mode === "forgot") {
      try {
        const { status, data } = await xhrRequest(`${API}/auth/forgot-password`, "POST", { email });
        if (status >= 400) {
          throw new Error(data.detail || "Failed to send reset email");
        }
        setForgotSuccess(true);
      } catch (err) {
        setError(err.message || "An error occurred. Please try again.");
      } finally {
        setIsLoading(false);
      }
      return;
    }

    try {
      const endpoint = mode === "login" ? "/auth/login" : "/auth/signup";
      
      // For signup, check for referral code in localStorage
      let body;
      let extraHeaders = {};
      
      if (mode === "login") {
        body = { email, password };
      } else {
        const referralCode = localStorage.getItem("referral_code");
        const visitorId = localStorage.getItem("visitor_id");
        
        body = { 
          email, 
          password, 
          name,
          referral_code: referralCode || null,
          terms_agreed: agreedToTerms,
          terms_agreed_at: new Date().toISOString()
        };
        
        // Include visitor ID to track conversion
        if (visitorId) {
          extraHeaders["X-Visitor-ID"] = visitorId;
        }
        
        // Clear the referral code after using it
        if (referralCode) {
          localStorage.removeItem("referral_code");
        }
      }

      const { status, data } = await xhrRequest(`${API}${endpoint}`, "POST", body, true, extraHeaders);

      if (status >= 400) {
        // Extract error message properly
        const errorMsg = typeof data?.detail === 'string' 
          ? data.detail 
          : (typeof data?.message === 'string' ? data.message : "Authentication failed");
        throw new Error(errorMsg);
      }

      // Handle email verification requirement for signup
      if (mode === "signup" && data.requires_verification) {
        setPendingEmail(email);
        setMode("verify");
        setIsLoading(false);
        return;
      }

      if (!data.user) {
        throw new Error("Invalid server response. Please try again.");
      }

      onSuccess(data.user);
      onClose();
    } catch (err) {
      console.error("Auth error:", err);
      // Ensure error is always a string
      const errorMessage = typeof err === 'string' 
        ? err 
        : (err?.message || "An unexpected error occurred. Please try again.");
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      const { status, data } = await xhrRequest(`${API}/auth/verify-email`, "POST", {
        email: pendingEmail,
        code: verificationCode
      });

      if (status >= 400) {
        const errorMsg = typeof data?.detail === 'string' 
          ? data.detail 
          : "Verification failed";
        throw new Error(errorMsg);
      }

      if (!data.user) {
        throw new Error("Invalid server response. Please try again.");
      }

      onSuccess(data.user);
      onClose();
    } catch (err) {
      console.error("Verification error:", err);
      const errorMessage = typeof err === 'string' 
        ? err 
        : (err?.message || "Invalid verification code. Please try again.");
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendCode = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const { status, data } = await xhrRequest(`${API}/auth/resend-verification`, "POST", {
        email: pendingEmail
      });

      if (status >= 400) {
        const errorMsg = typeof data?.detail === 'string' 
          ? data.detail 
          : "Failed to resend code";
        throw new Error(errorMsg);
      }

      setError(null);
      // Show success message briefly
      setError("Verification code resent!");
      setTimeout(() => setError(null), 3000);
    } catch (err) {
      const errorMessage = typeof err === 'string' 
        ? err 
        : (err?.message || "Failed to resend verification code.");
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `${GOOGLE_AUTH_URL}?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const toggleMode = () => {
    setMode(mode === "login" ? "signup" : "login");
    setError(null);
    setForgotSuccess(false);
    setAgreedToTerms(false);
  };

  const goToForgot = () => {
    setMode("forgot");
    setError(null);
    setForgotSuccess(false);
  };

  const backToLogin = () => {
    setMode("login");
    setError(null);
    setForgotSuccess(false);
  };

  if (!isOpen) return null;

  // Email Verification Step
  if (mode === "verify") {
    return (
      <>
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 animate-fade-in" onClick={onClose} />
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="bg-gradient-to-br from-zinc-900/95 via-zinc-900/90 to-zinc-800/80 backdrop-blur-xl border border-zinc-700/50 rounded-2xl w-full max-w-md p-6 animate-scale-in shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="text-center mb-6">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-violet-500/20 flex items-center justify-center">
                <Mail className="w-8 h-8 text-violet-400" />
              </div>
              <h2 className="text-xl font-bold text-white mb-2">Verify your email</h2>
              <p className="text-zinc-400 text-sm">
                We sent a 6-digit code to <span className="text-white">{pendingEmail}</span>
              </p>
            </div>

            <form onSubmit={handleVerifyCode} className="space-y-4">
              {error && (
                <div className={`p-3 rounded-lg text-sm ${
                  error.includes("resent") 
                    ? "bg-green-500/10 border border-green-500/30 text-green-400"
                    : "bg-red-500/10 border border-red-500/30 text-red-400"
                }`}>
                  {error}
                </div>
              )}

              <div>
                <label className="block text-xs font-medium text-zinc-500 uppercase tracking-wider mb-2">
                  Verification Code
                </label>
                <input
                  type="text"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value.replace(/[^0-9]/g, '').slice(0, 6))}
                  placeholder="Enter 6-digit code"
                  className="w-full px-4 py-3 bg-black border border-zinc-800 rounded-xl text-white placeholder-zinc-600 focus:outline-none focus:border-violet-500 transition-colors text-center text-2xl tracking-[0.5em] font-mono"
                  maxLength={6}
                  autoComplete="one-time-code"
                  required
                />
              </div>

              <button
                type="submit"
                disabled={isLoading || verificationCode.length !== 6}
                className="w-full py-3 bg-violet-600 hover:bg-violet-500 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold rounded-xl transition-all"
              >
                {isLoading ? (
                  <span className="flex items-center justify-center gap-2">
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Verifying...
                  </span>
                ) : (
                  "Verify & Create Account"
                )}
              </button>
            </form>

            <div className="mt-4 text-center">
              <p className="text-zinc-500 text-sm mb-2">
                Didn&apos;t receive the code?
              </p>
              <button
                onClick={handleResendCode}
                disabled={isLoading}
                className="text-violet-400 hover:text-violet-300 text-sm font-medium transition-colors"
              >
                Resend code
              </button>
            </div>

            <div className="mt-4 pt-4 border-t border-zinc-800 text-center">
              <button
                onClick={() => {
                  setMode("signup");
                  setVerificationCode("");
                  setError(null);
                }}
                className="text-zinc-500 hover:text-white text-sm transition-colors"
              >
                ← Back to signup
              </button>
            </div>
          </div>
        </div>
      </>
    );
  }

  // Forgot Password Success State
  if (mode === "forgot" && forgotSuccess) {
    return (
      <>
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 animate-fade-in" onClick={onClose} />
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="bg-gradient-to-br from-zinc-900/95 via-zinc-900/90 to-zinc-800/80 backdrop-blur-xl border border-zinc-700/50 rounded-2xl w-full max-w-md p-6 animate-scale-in shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="text-center py-6">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-500/20 flex items-center justify-center">
                <CheckCircle className="w-8 h-8 text-green-400" />
              </div>
              <h2 className="text-xl font-bold text-white mb-2">Check your email</h2>
              <p className="text-zinc-400 text-sm mb-6">
                If an account exists with <span className="text-white">{email}</span>, we've sent a password reset link.
              </p>
              <p className="text-zinc-500 text-xs">
                Didn't receive the email? Check your spam folder or try again.
              </p>
              <button
                onClick={backToLogin}
                className="mt-6 px-6 py-2 bg-white text-black font-semibold rounded-xl hover:bg-zinc-100 transition-all"
              >
                Back to login
              </button>
            </div>
          </div>
        </div>
      </>
    );
  }

  // Forgot Password Form
  if (mode === "forgot") {
    return (
      <>
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 animate-fade-in" onClick={onClose} />
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="bg-gradient-to-br from-zinc-900/95 via-zinc-900/90 to-zinc-800/80 backdrop-blur-xl border border-zinc-700/50 rounded-2xl w-full max-w-md p-6 animate-scale-in shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <button onClick={backToLogin} className="flex items-center gap-2 text-zinc-400 hover:text-white transition-colors">
                <ArrowLeft className="w-4 h-4" />
                <span className="text-sm">Back to login</span>
              </button>
              <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-zinc-800 transition-colors">
                <X className="w-5 h-5 text-zinc-400" />
              </button>
            </div>
            
            <h2 className="text-xl font-bold text-white mb-2">Reset your password</h2>
            <p className="text-zinc-400 text-sm mb-6">Enter your email address and we'll send you a link to reset your password.</p>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-zinc-400 uppercase tracking-wider mb-2">Email</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    required
                    className="w-full bg-black border border-zinc-800 rounded-xl pl-10 pr-4 py-3 text-white placeholder-zinc-600 focus:outline-none focus:border-zinc-600 transition-all"
                  />
                </div>
              </div>
              
              {error && (
                <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 text-red-400 text-sm">{error}</div>
              )}
              
              <button
                type="submit"
                disabled={isLoading}
                className="w-full py-3 bg-white text-black font-bold rounded-xl hover:bg-zinc-100 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isLoading ? (<><Loader2 className="w-4 h-4 animate-spin" />Sending...</>) : "Send reset link"}
              </button>
            </form>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 animate-fade-in"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div 
          className="bg-gradient-to-br from-zinc-900/95 via-zinc-900/90 to-zinc-800/80 backdrop-blur-xl border border-zinc-700/50 rounded-2xl w-full max-w-md p-6 animate-scale-in shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-white">
              {mode === "login" ? "Welcome back" : "Create account"}
            </h2>
            <button
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-zinc-800 transition-colors"
            >
              <X className="w-5 h-5 text-zinc-400" />
            </button>
          </div>

          {/* In-App Browser Warning */}
          {inAppBrowser && (
            <div className="mb-4 p-3 bg-amber-500/10 border border-amber-500/20 rounded-xl">
              <div className="flex items-start gap-3">
                <ExternalLink className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-amber-200 text-sm font-medium mb-1">
                    Open in browser for Google Sign-in
                  </p>
                  <p className="text-amber-200/70 text-xs leading-relaxed">
                    Google Sign-in doesn't work in {appName}'s browser. Tap the menu (⋯) and select <strong>"Open in Safari"</strong> or <strong>"Open in Browser"</strong>.
                  </p>
                  <p className="text-amber-200/70 text-xs mt-2">
                    Or use email/password below to sign in.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Google Sign In */}
          <button
            onClick={handleGoogleLogin}
            disabled={isLoading || inAppBrowser}
            className={`w-full flex items-center justify-center gap-3 px-4 py-3 font-semibold rounded-xl transition-all mb-4 ${
              inAppBrowser 
                ? "bg-zinc-700 text-zinc-400 cursor-not-allowed" 
                : "bg-white text-black hover:bg-zinc-100 disabled:opacity-50"
            }`}
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path fill={inAppBrowser ? "#666" : "#4285F4"} d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill={inAppBrowser ? "#666" : "#34A853"} d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill={inAppBrowser ? "#666" : "#FBBC05"} d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill={inAppBrowser ? "#666" : "#EA4335"} d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            {inAppBrowser ? "Google Sign-in unavailable" : "Continue with Google"}
          </button>

          {/* Divider */}
          <div className="flex items-center gap-3 mb-4">
            <div className="flex-1 h-px bg-zinc-800" />
            <span className="text-xs text-zinc-500">{inAppBrowser ? "use email instead" : "or"}</span>
            <div className="flex-1 h-px bg-zinc-800" />
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {mode === "signup" && (
              <div>
                <label className="block text-xs font-semibold text-zinc-400 uppercase tracking-wider mb-2">
                  Name
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Your name"
                    required
                    className="w-full bg-black border border-zinc-800 rounded-xl pl-10 pr-4 py-3 text-white placeholder-zinc-600 focus:outline-none focus:border-zinc-600 transition-all"
                  />
                </div>
              </div>
            )}

            <div>
              <label className="block text-xs font-semibold text-zinc-400 uppercase tracking-wider mb-2">
                Email
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                <input
                  type="email"
                  value={email}
                  onChange={handleEmailChange}
                  onBlur={handleEmailBlur}
                  placeholder="you@example.com"
                  required
                  className={`w-full bg-black border rounded-xl pl-10 pr-10 py-3 text-white placeholder-zinc-600 focus:outline-none transition-all ${
                    emailError 
                      ? 'border-red-500/50 focus:border-red-500' 
                      : 'border-zinc-800 focus:border-zinc-600'
                  }`}
                />
                {isValidatingEmail && (
                  <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500 animate-spin" />
                )}
                {!isValidatingEmail && mode === "signup" && email && !emailError && (
                  <Check className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-green-500" />
                )}
              </div>
              {emailError && mode === "signup" && (
                <p className="mt-1.5 text-xs text-red-400">{emailError}</p>
              )}
            </div>

            <div>
              <label className="block text-xs font-semibold text-zinc-400 uppercase tracking-wider mb-2">
                Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  minLength={6}
                  className="w-full bg-black border border-zinc-800 rounded-xl pl-10 pr-12 py-3 text-white placeholder-zinc-600 focus:outline-none focus:border-zinc-600 transition-all"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {mode === "login" && (
                <button
                  type="button"
                  onClick={goToForgot}
                  className="text-xs text-zinc-500 hover:text-white transition-colors mt-1"
                >
                  Forgot password?
                </button>
              )}
            </div>

            {/* Terms Agreement Checkbox - Only for signup */}
            {mode === "signup" && (
              <div className="flex items-start gap-3">
                <div className="flex items-center h-5 mt-0.5">
                  <input
                    id="terms-checkbox"
                    type="checkbox"
                    checked={agreedToTerms}
                    onChange={(e) => setAgreedToTerms(e.target.checked)}
                    className="w-4 h-4 rounded border-zinc-700 bg-black text-violet-600 focus:ring-violet-500 focus:ring-offset-0 focus:ring-2 cursor-pointer"
                  />
                </div>
                <label htmlFor="terms-checkbox" className="text-xs text-zinc-400 leading-relaxed cursor-pointer select-none">
                  I agree to the{" "}
                  <a 
                    href="/legal/terms" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-violet-400 hover:text-violet-300 underline"
                    onClick={(e) => e.stopPropagation()}
                  >
                    Terms of Service
                  </a>
                  {" "}and{" "}
                  <a 
                    href="/legal/privacy" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-violet-400 hover:text-violet-300 underline"
                    onClick={(e) => e.stopPropagation()}
                  >
                    Privacy Policy
                  </a>
                </label>
              </div>
            )}

            {error && (
              <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 text-red-400 text-sm">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-3 bg-white text-black font-bold rounded-xl hover:bg-zinc-100 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  {mode === "login" ? "Signing in..." : "Creating account..."}
                </>
              ) : (
                mode === "login" ? "Sign in" : "Create account"
              )}
            </button>
          </form>

          {/* Toggle mode */}
          <p className="text-center text-sm text-zinc-500 mt-4">
            {mode === "login" ? "Don't have an account?" : "Already have an account?"}{" "}
            <button
              onClick={toggleMode}
              className="text-white hover:underline font-medium"
            >
              {mode === "login" ? "Sign up" : "Sign in"}
            </button>
          </p>
        </div>
      </div>
    </>
  );
}

export function UserMenu({ user, onLogout }) {
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();

  const handleLogout = async () => {
    try {
      // Clear localStorage token
      localStorage.removeItem("session_token");
      
      // Also try to clear server session
      const token = localStorage.getItem("session_token");
      const xhr = new XMLHttpRequest();
      xhr.open("POST", `${API}/auth/logout`, true);
      if (token) {
        xhr.setRequestHeader("Authorization", `Bearer ${token}`);
      }
      xhr.withCredentials = true;
      xhr.send();
      
      onLogout();
    } catch (err) {
      console.error("Logout error:", err);
      localStorage.removeItem("session_token");
      onLogout(); // Still logout on frontend
    }
  };

  const handleNavigation = (path) => {
    setIsOpen(false);
    navigate(path);
  };

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        data-testid="user-menu-button"
        className={`flex items-center gap-2 px-3 py-1.5 bg-zinc-900 hover:bg-zinc-800 border rounded-lg transition-all ${
          user.is_admin ? 'border-red-500/30' : 'border-zinc-800'
        }`}
      >
        {user.picture ? (
          <img src={user.picture} alt="" className="w-6 h-6 rounded-full" />
        ) : (
          <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
            user.is_admin ? 'bg-red-500/20' : 'bg-zinc-700'
          }`}>
            <User className={`w-3 h-3 ${user.is_admin ? 'text-red-400' : 'text-zinc-400'}`} />
          </div>
        )}
        <span className="text-sm font-medium text-white max-w-[100px] truncate">
          {user.name?.split(" ")[0]}
        </span>
        {user.is_admin && (
          <span className="text-[8px] px-1 py-0.5 bg-red-500/20 text-red-400 rounded font-bold">
            ADMIN
          </span>
        )}
      </button>

      {isOpen && (
        <>
          <div 
            className="fixed inset-0 z-40"
            onClick={() => setIsOpen(false)}
          />
          <div className="absolute right-0 top-full mt-2 w-48 bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden shadow-xl z-50 animate-fade-in">
            <div className="p-3 border-b border-zinc-800">
              <p className="text-sm font-medium text-white truncate">{user.name}</p>
              <p className="text-xs text-zinc-500 truncate">{user.email}</p>
              {user.plan && (
                <span className={`inline-block mt-1 text-[10px] px-1.5 py-0.5 rounded font-semibold ${
                  user.plan === 'unlimited' ? 'bg-amber-500/20 text-amber-400' :
                  user.plan === 'pro' ? 'bg-violet-500/20 text-violet-400' :
                  user.plan === 'basic' ? 'bg-zinc-600 text-zinc-300' :
                  'bg-zinc-700 text-zinc-400'
                }`}>
                  {user.plan?.toUpperCase() || 'FREE'}
                </span>
              )}
            </div>
            {user.is_admin && (
              <button
                onClick={() => handleNavigation('/admin')}
                data-testid="admin-dashboard-link"
                className="block w-full px-3 py-2 text-left text-sm text-red-400 hover:bg-zinc-800 transition-colors border-b border-zinc-800"
              >
                🛡️ Admin Dashboard
              </button>
            )}
            <button
              onClick={() => handleNavigation('/settings')}
              data-testid="settings-link"
              className="block w-full px-3 py-2 text-left text-sm text-zinc-300 hover:bg-zinc-800 transition-colors border-b border-zinc-800"
            >
              ⚙️ Settings
            </button>
            <button
              onClick={() => handleNavigation('/dashboard')}
              data-testid="my-scripts-link"
              className="block w-full px-3 py-2 text-left text-sm text-zinc-300 hover:bg-zinc-800 transition-colors border-b border-zinc-800"
            >
              📁 My Scripts
            </button>
            <button
              onClick={handleLogout}
              data-testid="sign-out-button"
              className="w-full px-3 py-2 text-left text-sm text-red-400 hover:bg-zinc-800 transition-colors"
            >
              Sign out
            </button>
          </div>
        </>
      )}
    </div>
  );
}

export function GuestBanner({ remaining, limit, onSignUp, isGuest = true, plan = null }) {
  // Don't show banner for unlimited users
  if (plan === 'unlimited') {
    return null;
  }

  // Determine banner content based on user state
  const getBannerContent = () => {
    if (isGuest) {
      return {
        message: `${remaining}/${limit}`,
        buttonText: "Sign up"
      };
    }
    
    // Registered users with plans
    switch (plan) {
      case 'free':
      case null:
        return {
          message: `${remaining}/${limit}`,
          buttonText: "Plans"
        };
      case 'basic':
        return {
          message: `${remaining}/${limit}`,
          buttonText: "Upgrade"
        };
      case 'pro':
        return {
          message: `${remaining}/${limit}`,
          buttonText: "Unlimited"
        };
      default:
        return null;
    }
  };

  const content = getBannerContent();
  if (!content) return null;

  const isLow = remaining <= 1;

  return (
    <div className="inline-flex items-center gap-1.5 sm:gap-2 px-2 sm:px-3 py-1 sm:py-1.5 bg-zinc-900/80 border border-zinc-800 rounded-full">
      <div className={`flex items-center gap-1 text-[10px] sm:text-xs font-medium ${isLow ? 'text-amber-400' : 'text-zinc-400'}`}>
        <span className={`w-1.5 h-1.5 rounded-full ${isLow ? 'bg-amber-400' : 'bg-emerald-400'}`}></span>
        <span>{content.message}</span>
      </div>
      <div className="w-px h-3 bg-zinc-700"></div>
      <button
        onClick={onSignUp}
        className="text-[10px] sm:text-xs font-medium text-violet-400 hover:text-violet-300 transition-colors"
      >
        {content.buttonText}
      </button>
    </div>
  );
}
